import {Component, ChangeDetectionStrategy} from '@angular/core';
import {Dialog} from '../../../../../../../interfaces/dialog/dialog';
import {DialogParams} from '../../../../../../../interfaces/dialog/dialog-params';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {FunnelService} from '../../../../../../../services/cms/funnel.service';
import {DialogService} from '../../../../../../../services/dialog/dialog.service';
import {InputSwitchComponent} from '../../../../../../layout/input-switch/input-switch.component';
import {FunnelPage} from '../../../../../../../models/cms/funnel/funnel-page';

@Component({
  selector: 'app-funnel-url-editor',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputSwitchComponent
  ],
  templateUrl: './funnel-url-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './funnel-url-editor.component.scss'
})
export class FunnelUrlEditorComponent implements Dialog {
  page: FunnelPage | null = null;
  url: string = '';
  autoAddLeadUpParams: boolean = false;
  baseUrl: string = '';
  params: URLSearchParams = new URLSearchParams();
  variables: string[] = [];

  formAdd = new FormGroup({
    key: new FormControl('', Validators.required),
    value: new FormControl(''),
  });

  constructor(
    private funnelService: FunnelService,
    private dialogService: DialogService,
  ) {
    this.funnelService.fetchUrlVariables().subscribe(variables => {
      this.variables = variables;
    });
  }

  onDialogInit(params: DialogParams): void {
    this.page = params.data;
    this.url = this.page?.url ?? '';
    this.autoAddLeadUpParams = this.page?.options?.['autoAddLeadUpParams'] ?? false;
    this.parseUrl();
  }

  parseUrl(): void {
    const i = this.url.indexOf('?');
    if (i > -1) {
      this.baseUrl = this.url.substring(0, i);
    } else {
      this.baseUrl = this.url;
    }

    const url = new URL(this.url);
    this.params = url.searchParams;
  }

  updateUrl(): void {
    this.url = this.baseUrl;
    let first = true;
    for (const [key, value] of this.params) {
      this.url += first ? '?' : '&';
      this.url += encodeURIComponent(key) + '=';

      if (value && value.startsWith('{')) {
        // Don't encode variable
        this.url += value;
      } else {
        this.url += encodeURIComponent(value);
      }

      first = false;
    }
  }

  remove(param: any): void {
    this.params.delete(param[0]);
    this.updateUrl();
  }

  add(): void {
    if (this.formAdd.valid) {
      const key = this.formAdd.get('key')?.value ?? '';
      const value = this.formAdd.get('value')?.value ?? '';
      this.addUniqueParam(key, value);
      this.formAdd.setValue({
        key: '',
        value: '',
      });
      this.updateUrl();
    }
  }

  setParam(key: string, value: string): void {
    this.updateUrl();
  }

  addVariable(variable: string): void {
    this.formAdd.get('value')?.setValue('{' + variable + '}');
  }

  addEncryptedVariable(variable: string): void {
    this.addVariable('enc:' + variable);
  }

  update(): void {
    if (this.page) {
      this.page.url = this.url;
      this.page.options ??= {};
      this.page.options['autoAddLeadUpParams'] = this.autoAddLeadUpParams;
    }

    this.dialogService.closeDialog();
  }

  addAllParameters(): void {
    this.variables.forEach(variable => {
      this.addUniqueParam(variable, '{enc:' + variable + '}');
    });
    this.updateUrl();
  }

  addUniqueParam(key: string, value: string): void {
    if (this.params.has(key)) {
      this.params.set(key, value);
    } else {
      this.params.append(key, value);
    }
  }
}
